import React, { useEffect, useState } from 'react';
import { TrophyIcon, ChartBarIcon, StarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

interface LeaderboardEntry {
  rank: number;
  userId: string;
  name: string;
  profileImageUrl?: string;
  totalPoints: number;
  level: number;
}

const ReputationLeaderboard: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchLeaderboard();
    fetchCurrentUser();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/reputation/leaderboard/top', {
        params: { limit: 100 }
      });
      setLeaderboard(response.data);
    } catch (err: any) {
      console.error('Error fetching leaderboard:', err);
      setError(err.response?.data?.error || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setCurrentUserId(response.data.id);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const getPodiumColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'from-yellow-400 to-yellow-600';
      case 2:
        return 'from-gray-300 to-gray-500';
      case 3:
        return 'from-orange-400 to-orange-600';
      default:
        return 'from-blue-400 to-blue-600';
    }
  };

  const getMedalEmoji = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return `#${rank}`;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  const topThree = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <TrophyIcon className="h-8 w-8 text-yellow-500" />
          Reputation Leaderboard
        </h1>
        <p className="mt-2 text-gray-600">
          Top members ranked by reputation points
        </p>
      </div>

      {/* Podium for Top 3 */}
      {topThree.length > 0 && (
        <div className="mb-12">
          <div className="flex items-end justify-center gap-4 md:gap-8">
            {/* 2nd Place */}
            {topThree[1] && (
              <div className="flex flex-col items-center w-32 md:w-40">
                <div className="relative mb-4">
                  {topThree[1].profileImageUrl ? (
                    <img
                      src={topThree[1].profileImageUrl}
                      alt={topThree[1].name}
                      className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-gray-400 shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-gray-400 bg-gray-200 flex items-center justify-center shadow-lg">
                      <span className="text-2xl font-bold text-gray-600">
                        {topThree[1].name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-3xl">
                    🥈
                  </div>
                </div>
                <div className={`w-full bg-gradient-to-b ${getPodiumColor(2)} rounded-t-lg p-4 text-white text-center`}>
                  <div className="font-bold text-lg mb-1 truncate">{topThree[1].name}</div>
                  <div className="text-sm opacity-90">Level {topThree[1].level}</div>
                  <div className="text-xs mt-2 font-semibold">{topThree[1].totalPoints.toLocaleString()} pts</div>
                  <div className="h-20 md:h-24 bg-white bg-opacity-20 rounded mt-2"></div>
                </div>
              </div>
            )}

            {/* 1st Place */}
            {topThree[0] && (
              <div className="flex flex-col items-center w-32 md:w-40 -mb-8">
                <div className="relative mb-4">
                  {topThree[0].profileImageUrl ? (
                    <img
                      src={topThree[0].profileImageUrl}
                      alt={topThree[0].name}
                      className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-yellow-400 shadow-2xl"
                    />
                  ) : (
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-yellow-400 bg-yellow-100 flex items-center justify-center shadow-2xl">
                      <span className="text-3xl md:text-4xl font-bold text-yellow-600">
                        {topThree[0].name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-4xl">
                    🥇
                  </div>
                </div>
                <div className={`w-full bg-gradient-to-b ${getPodiumColor(1)} rounded-t-lg p-4 text-white text-center`}>
                  <div className="font-bold text-xl mb-1 truncate">{topThree[0].name}</div>
                  <div className="text-sm opacity-90">Level {topThree[0].level}</div>
                  <div className="text-xs mt-2 font-semibold">{topThree[0].totalPoints.toLocaleString()} pts</div>
                  <div className="h-32 md:h-40 bg-white bg-opacity-20 rounded mt-2"></div>
                </div>
              </div>
            )}

            {/* 3rd Place */}
            {topThree[2] && (
              <div className="flex flex-col items-center w-32 md:w-40">
                <div className="relative mb-4">
                  {topThree[2].profileImageUrl ? (
                    <img
                      src={topThree[2].profileImageUrl}
                      alt={topThree[2].name}
                      className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-orange-400 shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-orange-400 bg-orange-100 flex items-center justify-center shadow-lg">
                      <span className="text-2xl font-bold text-orange-600">
                        {topThree[2].name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-3xl">
                    🥉
                  </div>
                </div>
                <div className={`w-full bg-gradient-to-b ${getPodiumColor(3)} rounded-t-lg p-4 text-white text-center`}>
                  <div className="font-bold text-lg mb-1 truncate">{topThree[2].name}</div>
                  <div className="text-sm opacity-90">Level {topThree[2].level}</div>
                  <div className="text-xs mt-2 font-semibold">{topThree[2].totalPoints.toLocaleString()} pts</div>
                  <div className="h-20 md:h-24 bg-white bg-opacity-20 rounded mt-2"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rest of Leaderboard */}
      {rest.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Points
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rest.map((entry) => (
                <tr
                  key={entry.userId}
                  className={`${
                    entry.userId === currentUserId
                      ? 'bg-blue-50 border-l-4 border-blue-500'
                      : 'hover:bg-gray-50'
                  } transition-colors`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getMedalEmoji(entry.rank)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {entry.profileImageUrl ? (
                        <img
                          src={entry.profileImageUrl}
                          alt={entry.name}
                          className="h-10 w-10 rounded-full mr-3"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-gray-600">
                            {entry.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {entry.name}
                          {entry.userId === currentUserId && (
                            <span className="ml-2 text-xs text-blue-600 font-semibold">(You)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <StarIcon className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm text-gray-900">{entry.level}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <ChartBarIcon className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-semibold text-gray-900">
                        {entry.totalPoints.toLocaleString()}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {leaderboard.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <TrophyIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No leaderboard data available yet.</p>
        </div>
      )}
    </div>
  );
};

export default ReputationLeaderboard;
